import { useState } from 'react';
import { ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { useMonthSalesReports, type MonthReportFigure } from '../../hooks/useMonthSalesReports';
import { useControlCentreReports } from '../../hooks/useControlCentreReports';
import { formatCurrency } from '../../lib/utils';
import {
  CashCollectedReportModal,
  LeadsReportModal,
  SalesReportModal,
  SalesVelocityModal,
} from './ControlCentreReportModals';

function percentChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

function Trend({ pct }: { pct: number | null }) {
  if (pct === null || pct === 0) return null;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] sm:text-[12px] font-medium ${up ? 'text-green-700' : 'text-red-700'}`}>
      {up ? <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <TrendingDown className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
      {Math.abs(pct)}%
    </span>
  );
}

/** One label / value line under a card's headline. */
function Row({
  label,
  value,
  hint,
  trendPct,
  tone = 'default',
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  trendPct?: number | null;
  tone?: 'default' | 'warn' | 'info';
  title?: string;
}) {
  const valueColour = tone === 'warn' ? 'text-amber-700' : tone === 'info' ? 'text-blue-700' : 'text-gray-900';
  return (
    <span className="flex items-baseline justify-between gap-2 text-[12px] sm:text-[13px]" title={title}>
      <span className={`truncate ${tone === 'warn' ? 'text-amber-700' : 'text-gray-500'}`}>
        {label}
        {hint ? <span className="ml-1 hidden text-[12px] text-gray-400 tabular-nums sm:inline">· {hint}</span> : null}
      </span>
      <span className={`inline-flex shrink-0 items-baseline gap-1.5 font-medium tabular-nums ${valueColour}`}>
        {value}
        {trendPct !== undefined ? <span className="hidden sm:inline-flex"><Trend pct={trendPct} /></span> : null}
      </span>
    </span>
  );
}

/** White report card. Clickable when onClick is given, which opens the expanded report. */
function ReportCard({
  label,
  value,
  suffix,
  trendPct,
  loading,
  error,
  onClick,
  children,
}: {
  label: string;
  value: string;
  suffix?: string;
  trendPct?: number | null;
  loading: boolean;
  error?: string | null;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-3 text-left shadow-sm sm:rounded-2xl sm:px-5 sm:py-5 ${
        onClick ? 'transition-colors hover:bg-gray-50 active:scale-[0.995] touch-manipulation' : ''
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-medium text-gray-500 sm:text-[13px]">{label}</span>
        {onClick ? <ChevronRight className="h-3 w-3 shrink-0 text-gray-400 sm:h-3.5 sm:w-3.5" aria-hidden /> : null}
      </span>
      <span className="mt-1 flex min-w-0 items-baseline gap-1.5 sm:mt-2 sm:gap-2">
        <span className="truncate text-[17px] font-semibold leading-none tracking-tight text-gray-900 tabular-nums sm:text-[32px]">
          {loading ? '—' : error ? '—' : value}
        </span>
        {!loading && !error && suffix ? <span className="shrink-0 text-[10px] font-medium text-gray-500 sm:text-[13px]">{suffix}</span> : null}
        {!loading && !error && trendPct !== undefined ? <Trend pct={trendPct} /> : null}
      </span>
      <span className="mt-2.5 block space-y-1 border-t border-gray-100 pt-2.5 sm:mt-4 sm:space-y-1.5 sm:pt-3">
        {error ? <span className="block text-[12px] text-red-600 sm:text-[13px]">{error}</span> : children}
      </span>
    </Tag>
  );
}

function figureValue(figure: MonthReportFigure, total = figure.total): string {
  return figure.loading ? '—' : figure.error ? figure.error : formatCurrency(total);
}

/**
 * Control Centre metric cards, all built on the report system (run_report):
 * Scheduled this month, Cash collected, New leads this month, Sales velocity.
 */
export default function ControlCentreReportCards({ money, crm }: { money: boolean; crm: boolean }) {
  const { sales, salesLastMonth, scheduled } = useMonthSalesReports();
  const { cash, leads, velocity } = useControlCentreReports({ money, leads: crm });
  const [openModal, setOpenModal] = useState<'sales' | 'cash' | 'leads' | 'velocity' | null>(null);

  const scheduledLoading = sales.loading || scheduled.loading;
  const scheduledBlocked = Boolean(sales.error || scheduled.error);
  const scheduledTotal = sales.total + scheduled.total;
  const salesTrend = percentChange(sales.total, salesLastMonth);

  const cashTrend = percentChange(cash.collected, cash.lastMonth);
  const leadsTrend = percentChange(leads.leads, leads.lastMonth);
  const velocityTrend = percentChange(velocity.perDay, velocity.previousPerDay);
  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4" aria-label="Dashboard metrics">
        {money ? (
          <ReportCard
            label="Scheduled this month"
            value={formatCurrency(scheduledTotal)}
            loading={scheduledLoading || scheduledBlocked}
            onClick={() => setOpenModal('sales')}
          >
            <Row label="Sales" value={figureValue(sales)} trendPct={sales.loading || sales.error ? undefined : salesTrend} />
            <Row
              label="Scheduled"
              hint={!scheduled.loading && !scheduled.error ? plural(scheduled.upcoming.count, 'job') : undefined}
              value={figureValue(scheduled, scheduled.upcoming.total)}
              title="Jobs dated today or later, not yet invoiced"
            />
            {!scheduled.loading && !scheduled.error && scheduled.pastDue.count > 0 ? (
              <Row
                label="Past date, not invoiced"
                hint={plural(scheduled.pastDue.count, 'job')}
                value={formatCurrency(scheduled.pastDue.total)}
                tone="warn"
                title="Jobs whose date has passed and are still not completed or invoiced"
              />
            ) : null}
            <Row
              label="Avg sale"
              value={sales.loading || sales.error ? '—' : formatCurrency(sales.average)}
              title="Sales this month divided by the number of sales"
            />
          </ReportCard>
        ) : null}

        {money ? (
          <ReportCard
            label="Cash collected"
            value={formatCurrency(cash.collected)}
            trendPct={cashTrend}
            loading={cash.loading}
            error={cash.error}
            onClick={() => setOpenModal('cash')}
          >
            <Row label="Today" value={formatCurrency(cash.today)} />
            <Row label="Invoiced this month" value={formatCurrency(cash.invoiced)} />
            <Row
              label="Outstanding"
              hint={cash.openInvoices > 0 ? plural(cash.openInvoices, 'invoice') : undefined}
              value={formatCurrency(cash.outstanding)}
              tone={cash.outstanding > 0 ? 'warn' : 'default'}
              title="Unpaid balance on open invoices, as of today"
            />
            {cash.overdue > 0 ? (
              <Row label="Past due" value={formatCurrency(cash.overdue)} tone="warn" title="Open invoices past their due date" />
            ) : null}
          </ReportCard>
        ) : null}

        {crm ? (
          <ReportCard
            label="New leads this month"
            value={String(leads.leads)}
            trendPct={leadsTrend}
            loading={leads.loading}
            error={leads.error}
            onClick={() => setOpenModal('leads')}
          >
            <span className="flex items-baseline gap-2 text-[12px] font-medium tabular-nums sm:gap-3 sm:text-[13px]">
              <span className="text-blue-700">{leads.openNow} open</span>
              <span className="text-gray-900">{leads.won} won</span>
              <span className="text-red-700">{leads.lost} lost</span>
            </span>
            <Row label="Opening balance" value={String(leads.opening)} tone="info" title="Leads already open when the month started" />
            <Row label="Today" value={String(leads.today)} />
          </ReportCard>
        ) : null}

        {crm ? (
          <ReportCard
            label="Sales velocity"
            value={formatCurrency(velocity.perDay)}
            suffix="/day"
            trendPct={velocityTrend}
            loading={velocity.loading}
            onClick={() => setOpenModal('velocity')}
          >
            <Row label="Leads per day" value={velocity.leadsPerDay.toFixed(1)} />
            <Row label="Avg deal" value={formatCurrency(velocity.avgDeal)} title="Average won value over the last 180 days" />
            <Row
              label="Conversion"
              value={`${Math.round(velocity.conversion)}%`}
              title="Leads created 30 to 90 days ago that have been won"
            />
          </ReportCard>
        ) : null}
      </div>

      <SalesReportModal open={openModal === 'sales'} onClose={() => setOpenModal(null)} scheduled={scheduled} />
      <CashCollectedReportModal open={openModal === 'cash'} onClose={() => setOpenModal(null)} cash={cash} />
      <LeadsReportModal open={openModal === 'leads'} onClose={() => setOpenModal(null)} leads={leads} />
      <SalesVelocityModal
        open={openModal === 'velocity'}
        onClose={() => setOpenModal(null)}
        velocity={velocity}
        monthLabel={leads.monthLabel}
      />
    </>
  );
}
